import React, { FC, useCallback, useEffect, useMemo, useReducer } from 'react';

import { Clients, NotificationFor } from '@ariksa/notification';
import {
  ClientsApiSearchJiraFieldRequest,
  TicketFields,
  TicketFieldTypesEnum,
} from '@ariksa/notification/api';
import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { AxiosPromise } from 'axios';
import dayjs from 'dayjs';
import {
  each,
  flatten,
  forEach,
  includes,
  isArray,
  isEmpty,
  map,
  sum,
  throttle,
  unionBy,
  values,
} from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Dict } from 'types/utils';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import { AlertVaultJiraService } from 'api/alert-vault/jira';
import { NotificationService } from 'api/services';
import { DetailsIdentifier, StackedHeader } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import {
  CancelButton,
  Form,
  FormSchema,
  PrimaryButton,
  Select,
} from 'components/DataEntry';
import { IconTypes, QuestionCircleIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { errorToast, successToast } from 'components/Toast';
import { selectActiveEnvironment } from 'containers/App/selectors';
import { EnvironmentName } from 'containers/App/utils';
import { ticketingServices } from 'containers/Findings/Alerts/Components/utils';
import { selectAlerts } from 'containers/Findings/Alerts/selectors';
import { actions } from 'containers/Findings/Alerts/slice';
import { useSearchParams } from 'hooks/useSearchParams';

interface ICreateTicket {
  alert: Record<string, any>;
  notification_for: NotificationFor;
  onClose();
  isOpen: boolean;
  onCreate?(data: any, alert);
}

export const CreateTicket: FC<ICreateTicket> = props => {
  const { alert, notification_for, isOpen, onClose, onCreate } = props;
  const { environmentId: environment_id } = useSearchParams<{
    environmentId: string;
  }>();
  const { environmentId } = useSelector(selectActiveEnvironment);
  const dispatch = useDispatch();
  const { alertAction, ticketInfo, ticketFields, clients } = useSelector(
    selectAlerts,
  );

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      clientType: {},
      clientOptions: [],
      projectOptions: [],
      selectedProject: {},
      ticketFieldValues: {},
      updatedTicketFieldValues: {},
      ticketFieldFinalValues: {},
      projectType: 'array',
      summary: '',
      loadingFieldsForFirstTime: true,
      allTicketFields: [] as TicketFields[],
      isLoadingOptions: false,
      dueDate: null,
      labelsError: '',
      newLabelOptions: [],
    },
  );

  useEffect(() => {
    dispatch(actions.getClients({ q: {} }));
  }, [dispatch]);

  //set ticketing services (client) options and select first one as the default value
  useEffect(() => {
    const options = ticketingServices.filter(t =>
      clients.data?.find(c => c.client_name === t.value),
    );

    updateState({
      clientOptions: options,
      clientType: options[0],
    });
  }, [clients.data]);

  //set project options
  useEffect(() => {
    const options = clients.data
      ?.filter(c => c.client_name === state.clientType?.value)
      ?.map(c => ({
        label: c.name,
        value: c.uuid,
        data: c,
      }));
    updateState({
      projectOptions: options,
      selectedProject: options?.[0],
    });
  }, [clients.data, state.clientType]);

  //set default field value on successful fetching of fields
  const onSuccessSetClientFields = useCallback(res => {
    let options: Record<string, any> = {};
    let finalValues: Record<string, any> = {};
    forEach(res?.ticket_fields, o => {
      let value: any =
        o.type === TicketFieldTypesEnum.SingleSelect ||
        o.type === TicketFieldTypesEnum.Labels
          ? undefined
          : o.type === TicketFieldTypesEnum.MultiSelect
          ? []
          : o?.default;
      let finalValue =
        o.type === TicketFieldTypesEnum.MultiSelect
          ? o?.default_list ?? o?.default ?? []
          : o?.default;
      if (o.type === TicketFieldTypesEnum.Date) {
        value = !!o?.default ? dayjs(o?.default).local() : null;
        finalValue = value;
      } else if (
        o.type === TicketFieldTypesEnum.SingleSelect ||
        o.type === TicketFieldTypesEnum.Labels ||
        o.type === TicketFieldTypesEnum.MultiSelect
      ) {
        let items: Record<string, any> = {};
        each(
          o.options,
          (option: Record<string, any>) =>
            (items[
              option[o.field === 'assignee' || o.field === 'reporter' ? 1 : 0]
            ] = {
              label: option[1],
              value: option[0],
            }),
        );
        if (
          o.type === TicketFieldTypesEnum.SingleSelect ||
          o.type === TicketFieldTypesEnum.Labels
        ) {
          value = !!o?.default
            ? !isEmpty(items)
              ? items[o?.default!]
              : { label: o?.default, value: o?.default }
            : undefined;
        } else {
          const defaultValues = o?.default_list ?? o?.default ?? [];
          value = !!defaultValues
            ? !isEmpty(o?.options)
              ? map(defaultValues, o => items[o])
              : map(defaultValues, o => ({ label: o, value: o }))
            : [];
        }
      }
      const updatedValue = state.updatedTicketFieldValues?.[o?.field];
      options[o.field] = updatedValue || value;
      finalValues[o.field] = !!updatedValue
        ? o.type === TicketFieldTypesEnum.Textarea ||
          o.type === TicketFieldTypesEnum.Text
          ? updatedValue
          : o.type === TicketFieldTypesEnum.SingleSelect ||
            o.type === TicketFieldTypesEnum.Labels
          ? updatedValue?.value
          : o.type === TicketFieldTypesEnum.MultiSelect
          ? map(updatedValue, o => o?.value)
          : o.type === TicketFieldTypesEnum.Date
          ? dayjs(updatedValue).utc().format()
          : updatedValue
        : finalValue;
    });

    updateState({
      ticketFieldValues: options,
      ticketFieldFinalValues: finalValues,
      allTicketFields: res?.ticket_fields,
    });
  }, []); //please do not add any dependency, it reloads api

  //get mandatory fields and ticket information
  useEffect(() => {
    if (state.clientType?.value && state.selectedProject?.value) {
      if (state.clientType?.value === 'jira')
        dispatch(
          actions.getTicketFields({
            q: {
              clientName: state.clientType?.value,
              notificationFor: notification_for,
              clientId: state.selectedProject?.value,
              alertUuid:
                notification_for === NotificationFor.Alerts
                  ? alert?.uuid
                  : undefined,
              issueType: state.selectedProject?.data?.jira?.default_issue_type,
              groupUuid:
                notification_for === NotificationFor.GroupAlerts
                  ? alert?.group_id
                  : undefined,
              environmentId: environment_id ?? environmentId,
            },
            onSuccess: req => {
              updateState({ loadingFieldsForFirstTime: false });
              onSuccessSetClientFields(req);
            },
          }),
        );
      else
        dispatch(
          actions.getTicketInfo({
            q: {
              clientName: state.clientType?.value,
              name: state.selectedProject?.label,
              notificationFor: notification_for,
              resourceId: alert.entity_id,
              entityUuid: alert.entity_uuid,
              accountId: alert.account_id,
              resourceType: alert.entity_type,
              alertUuid: alert.uuid,
              alertRuleId: alert.alert_rule_id,
            },
            onSuccess: res => {
              updateState({ summary: res.summary });
            },
          }),
        );
    }
  }, [
    alert,
    state.selectedProject,
    state.clientType,
    dispatch,
    notification_for,
    onSuccessSetClientFields,
  ]);

  //reset everything on unmount
  useEffect(() => {
    return () => {
      dispatch(actions.resetTicketDetails());
    };
  }, [dispatch]);

  //if not valid, disable create button
  const isValid = useMemo(() => {
    if (
      state.clientType?.value === 'jira' &&
      ((isEmpty(state.ticketFieldFinalValues) &&
        !isEmpty(state.allTicketFields)) ||
        values(state.allTicketFields).some(
          v => !state.ticketFieldFinalValues?.[v.field] && v.required,
        ))
    )
      return false;

    return !!state.clientType?.value && !!state.selectedProject?.label;
  }, [
    state.selectedProject,
    state.clientType,
    state.ticketFieldFinalValues,
    state.allTicketFields,
  ]);

  //create ticket
  const handleCreateTicket = useCallback(
    data => {
      let ticket_fields: Dict<any> = {};
      each(ticketFields.data?.ticket_fields, o => {
        const val =
          o.type === TicketFieldTypesEnum.Date
            ? // Send the picked date; reading the form data sent today's date.
              state.ticketFieldFinalValues?.[o.field] || undefined
            : state.ticketFieldFinalValues?.[o.field];
        ticket_fields = {
          ...ticket_fields,
          ...(!!val && !isEmpty(val)
            ? {
                [o.field]:
                  val?.value ??
                  (isArray(val) ? map(val, a => a?.value ?? a) : val),
              }
            : {}),
        };
      });
      const onSuccess = () => {
        successToast({
          title:
            'Successfully created ticket for ' + state.selectedProject?.label,
        });
        onClose();
        onCreate?.(
          {
            ...data,
            clientType: state.clientType?.value,
            channelName: state.selectedProject?.label,
          },
          alert,
        );
      };

      const onError = error => {
        errorToast({
          title: 'Failed to create ticket for ' + state.selectedProject?.label,
          description: error?.description,
        });
        onClose();
      };

      if (state.clientType?.value === Clients.Jira) {
        dispatch(
          actions.createJiraTicket({
            q: {
              ticketCreateRequest: {
                client_name: state.clientType?.value,
                client_id: state.selectedProject?.value,
                alert_uuid:
                  notification_for === NotificationFor.Alerts
                    ? alert?.uuid
                    : undefined,
                account_id: alert.account_id,
                notification_for,
                ticket_create_fields: ticket_fields,
                environment_id: environment_id ?? environmentId,
                group_uuid:
                  notification_for === NotificationFor.GroupAlerts
                    ? alert?.group_id
                    : undefined,
              },
            },
            onSuccess,
            onError,
          }),
        );
      } else {
        dispatch(
          actions.createTicket({
            q: {
              notifyClients: {
                client_name: state.clientType?.value,
                client_id: state.selectedProject?.value,
                name: state.selectedProject?.label,
                alert_uuid: alert.uuid,
                entity_uuid: alert.entity_uuid,
                resource_id: alert.entity_id,
                account_id: alert.account_id,
                notification_for,
                ticket_fields,
              } as any,
            },
            onSuccess,
            onError,
          }),
        );
      }
    },
    [
      alert,
      state.selectedProject,
      state.clientType,
      dispatch,
      notification_for,
      onClose,
      onCreate,
      ticketFields.data,
      state.ticketFieldFinalValues,
      environment_id,
      environmentId,
    ],
  );

  const updateFieldValue = useCallback(
    (field, value, type) => {
      updateState({
        ticketFieldValues: {
          ...state.ticketFieldValues,
          [field]: value,
        },
        updatedTicketFieldValues: {
          ...state.updatedTicketFieldValues,
          [field]: value,
        },
        ticketFieldFinalValues: {
          ...state.ticketFieldFinalValues,
          [field]:
            type === TicketFieldTypesEnum.Textarea ||
            type === TicketFieldTypesEnum.Text
              ? value
              : type === TicketFieldTypesEnum.SingleSelect
              ? value?.value
              : type === TicketFieldTypesEnum.MultiSelect
              ? map(value, o => o?.value)
              : type === TicketFieldTypesEnum.Date
              ? // Store the picked date itself (was the unbound format function).
                dayjs(value).format('YYYY-MM-DD')
              : value,
        },
      });
    },
    [
      state.ticketFieldValues,
      state.updatedTicketFieldValues,
      state.ticketFieldFinalValues,
    ],
  );

  const querySuggestions = useCallback(
    async (promises: AxiosPromise<Record<string, any>[]>[]) => {
      return Promise.all(promises).then(results => {
        return map(
          results,
          res =>
            res.data?.map(o => {
              return {
                label: o?.value,
                value: o?.id,
              };
            }) ?? [],
        );
      });
    },
    [],
  );

  // Throttle API calls to avoid excessive requests
  const throttledLoadOptions = useCallback(
    throttle((inputValue, callback, field) => {
      const query = inputValue.trim();

      const payload: ClientsApiSearchJiraFieldRequest = {
        clientId: state.selectedProject?.value,
        field,
        query,
        issueTypeId: state.ticketFieldFinalValues?.['issuetype'],
        project: state.selectedProject?.data?.jira?.project_id,
      };

      // With Alert Vault the search runs against its stored Jira client.
      const loadQueryOptions = () =>
        isAlertVaultEnabled()
          ? AlertVaultJiraService.search(payload)
          : NotificationService.Clients.searchJiraField(payload);
      const promises: AxiosPromise<Record<string, any>[]>[] = [
        loadQueryOptions(),
      ];

      querySuggestions(promises)
        .then(suggestions => {
          const uniqSuggestions = unionBy(flatten(suggestions), 'id');
          callback(uniqSuggestions);
        })
        .catch(() => {});
    }, 2000), // Throttle interval (2s)
    [state.selectedProject, state.ticketFieldFinalValues],
  );

  //create form field object
  const fieldObject = useCallback(
    (field: TicketFields) => {
      let options = !!field?.options
        ? map(field?.options, v => ({
            label: v?.[1],
            value: v?.[0],
          }))
        : map(field.default_list, o => ({ label: o, value: o }));
      let labelOptions = map(field?.options, v => ({
        label: v,
        value: v,
      }));
      switch (field.type) {
        case TicketFieldTypesEnum.SingleSelect:
        case TicketFieldTypesEnum.MultiSelect:
          return {
            [field.field]: {
              type: 'react-select',
              label: field.title,
              ...(field.is_searchable
                ? {
                    helpIcon: (
                      <Box boxSize={4} color="primary">
                        <QuestionCircleIcon />
                      </Box>
                    ),
                    helpTooltip: 'Type to search',
                  }
                : {}),
              isRequired: field.required,
              isMulti: field.type === TicketFieldTypesEnum.MultiSelect,
              showTotalSelected:
                field.type === TicketFieldTypesEnum.MultiSelect,
              options,
              isDisabled: ticketFields.isLoading,
              async: field.is_searchable,
              loadOptions: (query, callback) => {
                throttledLoadOptions(query, callback, field.field);
              },
              value:
                state?.updatedTicketFieldValues?.[field.field] ||
                state?.ticketFieldValues?.[field.field] ||
                (field.type === TicketFieldTypesEnum.MultiSelect ? [] : {}),
              onChange: op => {
                if (field.updates_form) {
                  dispatch(
                    actions.getTicketFields({
                      q: {
                        clientName: state.clientType?.value,
                        notificationFor: notification_for,
                        clientId: state.selectedProject?.value,
                        alertUuid: alert.uuid,
                        issueType:
                          field.field === 'issuetype'
                            ? op?.label
                            : state?.ticketFieldFinalValues?.[field.field],
                      },
                      onSuccess: res => {
                        onSuccessSetClientFields(res);
                      },
                    }),
                  );
                }
                updateFieldValue(field.field, op, field.type);
              },
            },
          } as FormSchema;
        case TicketFieldTypesEnum.Labels:
          return {
            [field.field]: {
              type: 'react-select',
              label: field.title,
              creatable: true,
              isRequired: field.required,
              isMulti: true,
              tooltip: `Labels can't have spaces.`,
              options: [...labelOptions, ...state.newLabelOptions],
              isDisabled: ticketFields.isLoading,
              async: field.is_searchable,
              loadOptions: (query, callback) => {
                throttledLoadOptions(query, callback, field.field);
              },
              errorTooltipStyles: { ml: 14 },
              value:
                state?.updatedTicketFieldValues?.[field.field] ||
                state?.ticketFieldValues?.[field.field] ||
                [],
              error: state.labelError,
              onChange: op => {
                updateState({ labelError: '' });
                updateFieldValue(field.field, op, field.type);
              },
              onCreateOption: label => {
                if (includes(label, ' '))
                  updateState({
                    labelError: `Labels can't have spaces.`,
                  });
                else {
                  updateState({
                    newLabelOptions: [
                      ...state.newLabelOptions,
                      { label, value: label },
                    ],
                    labelError: '',
                  });
                  updateFieldValue(
                    field.field,
                    [
                      ...(state?.ticketFieldValues?.[field.field] ?? []),
                      { label, value: label },
                    ],
                    field.type,
                  );
                }
              },
            },
          } as FormSchema;
        case TicketFieldTypesEnum.Text:
        case TicketFieldTypesEnum.Textarea:
          return {
            [field.field]: {
              type:
                field.type === TicketFieldTypesEnum.Text ? 'text' : 'textArea',
              label: field.title,
              isRequired: field.required,
              value:
                state?.updatedTicketFieldValues?.[field.field] ??
                state?.ticketFieldValues?.[field.field],
              isDisabled: ticketFields.isLoading,
              onChange: value => {
                updateFieldValue(field.field, value, field.type);
              },
              styles: {
                textArea: {
                  h: '150px',
                  fontSize: 14,
                },
              },
            },
          } as FormSchema;
        case TicketFieldTypesEnum.Date:
          return {
            [field.field]: {
              type: 'dayPicker',
              label: field.title,
              isRequired: field.required,
              isDisabled: ticketFields.isLoading,
              dateProps: {
                mode: 'single',
                selected:
                  state?.updatedTicketFieldValues?.[field.field] ||
                  state?.ticketFieldValues?.[field.field] ||
                  null,
                onSelect: value => {
                  updateFieldValue(field.field, value, field.type);
                },
              },
            },
          } as FormSchema;
        default:
          return {};
      }
    },
    [
      state.ticketFieldValues,
      ticketFields.isLoading,
      onSuccessSetClientFields,
      state?.updatedTicketFieldValues,
      state?.ticketFieldFinalValues,
      alert.uuid,
      dispatch,
      notification_for,
      state.clientType,
      state.selectedProject,
      updateFieldValue,
      throttledLoadOptions,
      state.newLabelOptions,
      state.labelError,
    ],
  );

  const customFields = useMemo(() => {
    let fields: FormSchema = {};

    each(state.allTicketFields, field => {
      fields = { ...fields, ...fieldObject(field) };
    });

    return fields;
  }, [fieldObject, state.allTicketFields]);

  return (
    <Box>
      <Modal
        styles={{ modal: { size: '2xl' } }}
        isOpen={isOpen}
        onClose={onClose}
        header={
          <HStack justify="space-between" w="full">
            <StackedHeader
              upper="Create Ticket for Findings and Issues"
              lower={
                notification_for === NotificationFor.GroupAlerts
                  ? sum(Object.values(alert?.severity)) +
                    ' issue' +
                    (sum(Object.values(alert?.severity)) > 1 ? 's' : '')
                  : (alert.entity_name ?? '') +
                    (alert?.entity_id ? ' (' + alert.entity_id + ')' : '')
              }
              type={IconTypes.Ticket}
            />
          </HStack>
        }
        body={
          <Stack spacing={6}>
            <Stack spacing={6}>
              {notification_for === NotificationFor.Alerts && (
                <DetailsIdentifier
                  label="Policy blueprint"
                  value={alert.policy_name}
                />
              )}
              <DetailsIdentifier
                label="Environment"
                value={
                  <EnvironmentName
                    environmentId={environment_id ?? environmentId}
                  />
                }
              />
              <DetailsIdentifier
                label="Select client"
                align="center"
                value={
                  <Box w="200px">
                    <Select
                      options={state.clientOptions}
                      value={state.clientType}
                      onChange={opt =>
                        updateState({ clientType: opt, selectedProject: {} })
                      }
                      isLoading={clients.isLoading}
                      menuPortalTarget={document.body}
                      showClearIndicator={false}
                    />
                  </Box>
                }
              />
              <DetailsIdentifier
                label="Select project"
                align="center"
                value={
                  <Box w="200px">
                    <Select
                      isDisabled={clients.isLoading}
                      options={state.projectOptions}
                      isLoading={clients?.isLoading}
                      value={state.selectedProject}
                      onChange={opt => updateState({ selectedProject: opt })}
                      menuPortalTarget={document.body}
                      showClearIndicator={false}
                    />
                  </Box>
                }
              />
            </Stack>
            {ticketFields.isLoading && state.loadingFieldsForFirstTime ? (
              <Center h={64}>
                <CustomSpinner />
              </Center>
            ) : (
              <Form
                schema={{
                  ...(state.clientType?.value === 'servicenow'
                    ? {
                        summary: {
                          type: 'text',
                          label: 'Summary',
                          placeholder: 'Alert Summary',
                          value: state.summary,
                          defaultValue: state.summary,
                          onChange: v => updateState({ summary: v }),
                        },
                        description: {
                          type: 'textArea',
                          label: 'Description',
                          value: ticketInfo.data.description ?? '',
                          isDisabled: true,
                          styles: {
                            textArea: {
                              h: '150px',
                              fontSize: 14,
                            },
                          },
                        },
                      }
                    : customFields),
                }}
                buttonOptions={{
                  back: {
                    isVisible: false,
                    name: 'Cancel',
                    onClick: onClose,
                  },
                  submit: {
                    name: 'Create',
                    isVisible: false,
                  },
                }}
                handleSubmit={handleCreateTicket}
              />
            )}
          </Stack>
        }
        footer={
          <HStack w="full" justify="space-between">
            <CancelButton onClick={onClose}>CANCEL</CancelButton>
            <PrimaryButton
              onClick={handleCreateTicket}
              isDisabled={!isValid || ticketFields.isLoading}
              isLoading={alertAction.isLoading || ticketFields.isLoading}
            >
              CREATE
            </PrimaryButton>
          </HStack>
        }
      />
    </Box>
  );
};
